import { Button, Card, EmptyState, Input } from "@closer/ui";
import { useCallback, useEffect, useState } from "react";

import {
  basligiDegistir,
  maddeCogalt,
  maddeEkle,
  maddeleriOku,
  type MaddeSatiri,
  maddeSil,
  type OtomasyonHost,
  seviyeDegistir,
} from "./maddeAksiyonlari";

/**
 * MADDE PANELİ (CLOS-1256 — DOCX Kanonu §10).
 *
 * ### ⚠ Panel bir LİSTE değil, belgenin kendisi
 *
 * Kanon §10.3: *"Sahte sidebar clause listesi değişip document değişmeden kalamaz."* Bu
 * yüzden panel kendi durumunu güncellemiyor: her aksiyondan sonra maddeleri **belgeden
 * yeniden okuyor**. Ekranda gördüğünüz sıra, dosyadaki sıra.
 *
 * ### ⚠ Ayrı sayfa YOK (§10.1)
 *
 * Aksiyonlar belgenin yanında; kullanıcı metni terk etmiyor.
 *
 * ### ⚠ TAŞIMA DÜĞMESİ YOK ve sebebi ölçüldü
 *
 * Motorun otomasyon sözlüğünde blok taşıyan komut yok; sil+yeniden ekle ile taşımak bloğun
 * kimliğini (`w:bookmark`) yok eder ve kaydetmede üretilen redline o maddeyi "silindi +
 * eklendi" gösterirdi (CLOS-1255). Yani yanlış taşıma, müzakere kaydına olmayan bir hamle
 * yazardı. Düğmeyi koyup sessizce bunu yapmaktansa koymuyoruz ve sebebini ekranda yazıyoruz.
 */

export function ContractClausePanel({
  host,
  onDegisti,
}: {
  host: OtomasyonHost | null;
  /** Belge değiştiğinde üst ekranın haberi olsun (kaydedilmemiş değişiklik göstergesi). */
  onDegisti?: () => void;
}) {
  const [maddeler, setMaddeler] = useState<MaddeSatiri[]>([]);

  const tazele = useCallback(() => {
    setMaddeler(host ? maddeleriOku(host) : []);
  }, [host]);

  useEffect(() => {
    tazele();
  }, [tazele]);

  /** ⚠ Her aksiyon aynı kapıdan: belge değişti → listeyi BELGEDEN yeniden oku. */
  const uygula = (is: () => boolean) => {
    if (!host) return;
    const oldu = is();
    tazele();
    if (oldu) onDegisti?.();
  };

  if (!host) return null;

  return (
    <Card title="Maddeler">
      {maddeler.length === 0 ? (
        <EmptyState
          title="Bu belgede başlıklı madde yok"
          description="Başlık stili taşıyan paragraflar burada madde olarak listelenir."
        />
      ) : (
        <ul className="flex flex-col gap-3" data-testid="madde-listesi">
          {maddeler.map((madde, sira) => (
            <li
              key={`${sira}-${madde.baslik}`}
              className="flex flex-col gap-2 border-b border-border pb-3 last:border-0"
            >
              <div className="flex items-center gap-2">
                {/* ⚠ Girinti SEVİYEYİ gösteriyor: alt madde göz hizasında ayırt edilsin. */}
                <span
                  className="text-xs text-fg-faint"
                  style={{ width: `${(madde.seviye - 1) * 12}px` }}
                />
                <Input
                  aria-label={`${madde.baslik} başlığı`}
                  defaultValue={madde.baslik}
                  onBlur={(e) => {
                    const yeni = e.currentTarget.value;
                    if (yeni !== madde.baslik) uygula(() => basligiDegistir(host, madde, yeni));
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    uygula(() =>
                      maddeEkle(host, {
                        anchor: madde.handle,
                        nere: "before",
                        baslik: "Yeni madde",
                        seviye: madde.seviye,
                      }),
                    )
                  }
                >
                  Üste madde ekle
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    uygula(() =>
                      maddeEkle(host, {
                        anchor: madde.govde.at(-1) ?? madde.handle,
                        nere: "after",
                        baslik: "Yeni madde",
                        seviye: madde.seviye,
                      }),
                    )
                  }
                >
                  Alta madde ekle
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    uygula(() =>
                      maddeEkle(host, {
                        anchor: madde.handle,
                        nere: "after",
                        baslik: "Yeni alt madde",
                        seviye: Math.min(3, madde.seviye + 1),
                      }),
                    )
                  }
                >
                  Alt madde ekle
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    uygula(() =>
                      /** ⚠ Gövde metinleri BELGEDEN okunuyor; panel kendi kopyasını tutmuyor. */
                      maddeCogalt(
                        host,
                        madde,
                        madde.govde.map((_, i) => metinOku(host, madde, i)),
                      ),
                    )
                  }
                >
                  Çoğalt
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    uygula(() => seviyeDegistir(host, madde, madde.seviye === 1 ? 2 : 1))
                  }
                >
                  {madde.seviye === 1 ? "Alt maddeye çevir" : "Üst maddeye çevir"}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => uygula(() => maddeSil(host, madde))}
                >
                  Sil
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/*
        ⚠ EKSİK OLAN SÖYLENİYOR. Taşıma kanonun §10.2 listesinde var ama motorun
        sözlüğünde yok; sessiz bırakmak, kullanıcının aramayıp bulamadığı bir yetenek
        bırakmak olurdu.
      */}
      <p className="mt-3 text-xs text-fg-faint" data-testid="tasima-notu">
        Madde taşıma bu sürümde yok: taşıma, maddenin değişiklik geçmişindeki kimliğini
        kaybettirdiği için kapalı tutuluyor.
      </p>
    </Card>
  );
}

/** Bir maddenin `i`. gövde paragrafının metni — çoğaltma bunu kullanıyor. */
function metinOku(host: OtomasyonHost, madde: MaddeSatiri, i: number): string {
  const hedef = madde.govde[i];
  if (!hedef) return "";
  const sonuc = host.execute({ operations: [{ op: "getText", target: hedef }] }).results[0];
  const deger = sonuc?.status === "ok" ? (sonuc.value as { text?: string })?.text : "";
  return deger ?? "";
}
